/**
 * SA-717 (email triage spec T24): how triage is doing on one mailbox. Per
 * proposed kind: what waits, what a rule or the model decided, what people
 * approved or changed, and the agreement. Then the messages with no
 * proposal, how the model's confidence held up, and what the handlers did.
 * Read-only: thresholds and switches change on the routing editor (R14).
 */
import { useState } from 'react'
import { Button, Spinner } from '@/components/ui'
import { useTriageMetrics } from '@/hooks/useMailboxes'
import { calibrationLine, isEmpty, kindRows, outcomeLines, unknownLine, waitingNote } from './triageMetricsModel'

const TH = 'px-2 py-1 text-left text-[11px] font-semibold uppercase tracking-wide text-muted-foreground'
const TD = 'px-2 py-1 text-xs text-foreground tabular-nums'

export function TriageMetricsPanel({ mailboxId }: { mailboxId: string }) {
  const [days, setDays] = useState<30 | 90>(30)
  const query = useTriageMetrics(mailboxId, days)
  const metrics = query.data

  return (
    <div className="mt-3 rounded-md border border-border bg-muted/30 p-3 space-y-3">
      <div role="group" aria-label="Window" className="flex items-center gap-1">
        {([30, 90] as const).map((value) => (
          <Button key={value} type="button" size="sm" variant={days === value ? 'secondary' : 'ghost'}
            aria-pressed={days === value} onClick={() => setDays(value)}>
            {value} days
          </Button>
        ))}
      </div>
      {metrics === undefined && query.isLoading && <Spinner size="sm" />}
      {/* Unavailable only with nothing to show: a failed refetch keeps the last numbers. */}
      {metrics === undefined && query.isError && (
        <p className="text-xs text-muted-foreground">Triage metrics are unavailable.</p>
      )}
      {metrics !== undefined && isEmpty(metrics) && (
        <p className="text-xs text-muted-foreground">No message was triaged in this window.</p>
      )}
      {metrics !== undefined && !isEmpty(metrics) && (
        <>
          {kindRows(metrics).length > 0 && (
            <>
              <table className="w-full border-collapse">
                <thead>
                  <tr>
                    <th className={TH}>Proposed as</th>
                    <th className={TH}>Waiting</th>
                    <th className={TH}>Automatic</th>
                    <th className={TH}>Approved</th>
                    <th className={TH}>Changed</th>
                    <th className={TH}>Agreement</th>
                  </tr>
                </thead>
                <tbody>
                  {kindRows(metrics).map((row) => (
                    <tr key={row.kind} className="border-t border-border">
                      <td className={TD}>{row.label}</td>
                      <td className={TD}>{row.waiting}</td>
                      <td className={TD}>{row.auto}</td>
                      <td className={TD}>{row.approved}</td>
                      <td className={TD} title={row.changedTo ? `Changed to: ${row.changedTo}` : undefined}>{row.changed}</td>
                      <td className={TD}>{row.agreement}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <p className="text-xs text-muted-foreground">{waitingNote(metrics.days)}</p>
            </>
          )}
          {unknownLine(metrics) && <p className="text-xs text-foreground">{unknownLine(metrics)}</p>}
          <p className="text-xs text-muted-foreground">{calibrationLine(metrics)}</p>
          {outcomeLines(metrics).map((line) => <p key={line} className="text-xs text-muted-foreground">{line}</p>)}
        </>
      )}
    </div>
  )
}
